import React from "react";
import styled from "styled-components";
import { Icon } from "../Icon";
import * as SelectMenu from "../SelectMenu_v2";
import { style } from "../style";
import { RadioButton } from "./RadioButton";
import { useRadioGroupState } from "./RadioGroupStateContext";
import { useSelectedMenuOption } from "./hooks/useSelectedMenuOption";
import { LooseInteractionButton } from "../__Internal__";
import { useEvent } from "../hooks/useEvent";

type Placeholder = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
};

type Option = {
  value: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: string;
  group?: string;
  disabled?: boolean;
};

type Props = {
  /**
   * The default title to present when no option is selected.
   */
  placeholder: Placeholder;

  /**
   * The options that appear in the overflow menu.
   */
  options: Option[];

  /**
   * The default value to select from the dropdown.
   * @default null
   */
  defaultValue?: string | null;

  /**
   * Whether the radio button is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Testing utility tag.
   * @default "radio-button-with-overflow"
   */
  "data-testid"?: string;
};

type SelectMenuRef = {
  show(): void;
  hide(): void;
  toggle(): void;
  open: boolean;
};

const DropdownMenuWrapper = styled.div<{ $checked: boolean; $disabled: boolean }>`
  all: unset;

  border-left: ${style.vars.borderWidth.thin} solid
    ${(props) => (props.$checked && !props.$disabled ? style.colors.blue.dark : style.colors.grey.dark)};

  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;

  height: 63px;

  margin-top: calc(-8px - ${style.vars.borderWidth.thin});
  margin-bottom: calc(-8px - ${style.vars.borderWidth.thin});
`;

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.placeholder?.title === nextProps.placeholder?.title &&
    prevProps.placeholder?.subtitle === nextProps.placeholder?.subtitle &&
    prevProps.options === nextProps.options &&
    prevProps.defaultValue === nextProps.defaultValue &&
    prevProps.disabled === nextProps.disabled
  );
};

export const RadioButtonWithOverflow: React.FC<Props> = React.memo((props) => {
  const { placeholder, options } = props;
  const testId = props["data-testid"] ?? "radio-button-with-overflow";
  const menuRef = React.useRef<SelectMenuRef>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const state = useRadioGroupState();
  const option = React.useMemo(() => options.find((option) => option.value === state.selected), [options, state.selected]);

  const defaultValue = props.defaultValue ?? option?.value ?? null;
  const [selectedOption, setSelectedOption] = useSelectedMenuOption(options, { ...placeholder, value: defaultValue });

  const disabled = state.disabled || props.disabled || false;
  const checked = state.selected !== null && state.selected === selectedOption.value;

  const handleSelect = useEvent((value: string | null) => {
    setSelectedOption(value);
    state.radio.setValue(value);

    /**
     * We need to wait until the next tick to click the input, otherwise the
     * menu will capture the focus when the menu is closed.
     */
    queueMicrotask(() => inputRef.current?.focus());
  });

  const handleFocus = useEvent(() => {
    if (selectedOption.value === null) {
      menuRef.current?.show();
    }
  });

  /**
   * Toggle open the menu by pressing Space when this Radio is focused.
   */
  const handleKeyPress = useEvent<React.KeyboardEventHandler<HTMLElement>>((ev) => {
    ev.preventDefault();

    if (ev.key === " " && !disabled) {
      menuRef.current?.toggle();
    }
  });

  const rightSide = React.useMemo(() => {
    const node = (
      <DropdownMenuWrapper $checked={checked} $disabled={disabled}>
        <SelectMenu.Headless
          name={state.name}
          selected={state.selected}
          onSelect={handleSelect}
          width="large"
          options={options}
          ref={menuRef}
          disabled={disabled}
          closeOnEscape={selectedOption.value !== null}
        >
          {(params) => {
            return (
              <LooseInteractionButton
                data-testid={`${testId}/button`}
                as="div"
                borderRadiusTopRight="base"
                borderRadiusBottomRight={params.open ? "none" : "base"}
                disabled={params.disabled}
                borderTop={false}
                borderRight={false}
                borderBottom={false}
                borderLeft={false}
                paddingX="17px"
              >
                <Icon blue={params.open} icon={params.open ? "arrow-up" : "arrow-down"} />
              </LooseInteractionButton>
            );
          }}
        </SelectMenu.Headless>
      </DropdownMenuWrapper>
    );

    return {
      upper: node,
      hoist: true,
    };
  }, [checked, disabled, handleSelect, options, selectedOption.value, state.name, state.selected]);

  return (
    <RadioButton
      data-testid={testId}
      title={selectedOption.title}
      subtitle={selectedOption.subtitle}
      icon={selectedOption.icon}
      value={selectedOption.value}
      rightSide={rightSide}
      disabled={disabled}
      ref={inputRef}
      onKeyPress={handleKeyPress}
      onFocus={handleFocus}
      UNSAFE_flatBottomRight={menuRef.current?.open}
    />
  );
}, propsAreEqual);

RadioButtonWithOverflow.displayName = "RadioButtonWithOverflow";
